import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type RepeatMode = 'off' | 'all' | 'one'

export type QueueTrack = {
  episodeId: number
  title: string
  streamUrl: string
  index: number
  thumbnailPath: string | null
  playCount: number
  artist?: string
  album?: string
}

/**
 * 再生履歴の1件。
 * 旧プレイヤー(/p)・新しい音楽画面(/music)の両方から記録するため、
 * QueueTrack に依存しない最小限のフィールドのみを持つ。
 */
export type PlayHistoryEntry = {
  episodeId: number
  title: string
  thumbnailPath: string | null
  artist?: string
  album?: string
  /** サムネのキャッシュキーに使う商品ID */
  productId?: number
  playedAt: number
}

const MAX_HISTORY = 100

type MusicPlayerState = {
  queue: QueueTrack[]
  currentIndex: number
  shuffle: boolean
  repeatMode: RepeatMode
  playHistory: PlayHistoryEntry[]
  /** シャッフル前のupcomingトラック順序（シャッフル解除時に復元用） */
  _originalUpcoming: QueueTrack[] | null
}

type MusicPlayerActions = {
  initQueue: (tracks: QueueTrack[], startIndex: number) => void
  addToQueue: (tracks: QueueTrack[]) => number
  setCurrentIndex: (index: number) => void
  toggleShuffle: () => void
  cycleRepeatMode: () => void
  getNextIndex: () => number | null
  getPrevIndex: () => number | null
  reorderQueue: (oldIndex: number, newIndex: number) => void
  updatePlayCounts: (countMap: Map<number, number>) => void
  incrementTrackPlayCount: (episodeId: number) => void
  recordPlayHistory: (entry: Omit<PlayHistoryEntry, 'playedAt'>) => void
  resetQueue: () => void
  skipToTrack: (targetIndex: number) => void
  clearHistory: () => void
  clear: () => void
}

const REPEAT_CYCLE: RepeatMode[] = ['off', 'all', 'one']

export const useMusicPlayerStore = create<MusicPlayerState & MusicPlayerActions>()(
  persist(
    (set, get) => ({
      queue: [],
      currentIndex: 0,
      shuffle: false,
      repeatMode: 'off',
      playHistory: [],
      _originalUpcoming: null,

      initQueue: (tracks, startIndex) => {
        const { queue: existingQueue, currentIndex: existingCurrentIndex } = get()
        const targetEpisodeId = tracks[startIndex]?.episodeId

        // 同じトラックセットならキュー順序・再生履歴を保持し、currentIndexだけ更新
        const existingIds = new Set(existingQueue.map((t) => t.episodeId))
        const newIds = new Set(tracks.map((t) => t.episodeId))
        const isSameTrackSet = existingIds.size === newIds.size && [...newIds].every((id) => existingIds.has(id))

        if (isSameTrackSet && existingQueue.length > 0) {
          const idx = existingQueue.findIndex((t) => t.episodeId === targetEpisodeId)
          if (idx >= 0) {
            set({ currentIndex: idx })
            return
          }
        }

        // 別アルバム → 再生済み部分を保持し、新しいアルバムのトラックをupcomingに差し替え
        const played = existingQueue.slice(0, existingCurrentIndex + 1)
        const reordered = [...tracks.slice(startIndex), ...tracks.slice(0, startIndex)]
        const newQueue = [...played, ...reordered].map((t, i) => ({ ...t, index: i + 1 }))
        set({ queue: newQueue, currentIndex: played.length })
      },

      addToQueue: (tracks) => {
        const { queue } = get()
        const existingIds = new Set(queue.map((t) => t.episodeId))
        const newTracks = tracks.filter((t) => !existingIds.has(t.episodeId))
        if (newTracks.length === 0) return 0
        const reindexed = newTracks.map((t, i) => ({
          ...t,
          index: queue.length + i + 1,
        }))
        set({ queue: [...queue, ...reindexed] })
        return reindexed.length
      },

      setCurrentIndex: (index) => {
        set({ currentIndex: index })
      },

      toggleShuffle: () => {
        const { queue, currentIndex, shuffle, _originalUpcoming } = get()
        const played = queue.slice(0, currentIndex + 1)

        if (!shuffle) {
          // シャッフルON: upcoming を保存してシャッフル
          const upcoming = queue.slice(currentIndex + 1)
          const shuffled = [...upcoming]
          for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1))
            const tmp = shuffled[i]
            shuffled[i] = shuffled[j]
            shuffled[j] = tmp
          }
          const newQueue = [...played, ...shuffled].map((t, i) => ({ ...t, index: i + 1 }))
          set({ shuffle: true, queue: newQueue, _originalUpcoming: upcoming })
        } else {
          // シャッフルOFF: 元の順序に復元
          const restored = _originalUpcoming ?? queue.slice(currentIndex + 1)
          const newQueue = [...played, ...restored].map((t, i) => ({ ...t, index: i + 1 }))
          set({ shuffle: false, queue: newQueue, _originalUpcoming: null })
        }
      },

      cycleRepeatMode: () => {
        const { repeatMode } = get()
        const idx = REPEAT_CYCLE.indexOf(repeatMode)
        set({ repeatMode: REPEAT_CYCLE[(idx + 1) % REPEAT_CYCLE.length] })
      },

      getNextIndex: () => {
        const { queue, currentIndex, repeatMode } = get()
        if (queue.length === 0) return null

        if (repeatMode === 'one') return currentIndex

        const nextIdx = currentIndex + 1
        if (nextIdx < queue.length) return nextIdx
        if (repeatMode === 'all') return 0
        return null
      },

      getPrevIndex: () => {
        const { queue, currentIndex, repeatMode } = get()
        if (queue.length === 0) return null

        if (repeatMode === 'one') return currentIndex

        if (currentIndex - 1 >= 0) return currentIndex - 1
        if (repeatMode === 'all') return queue.length - 1
        return null
      },

      reorderQueue: (oldIndex, newIndex) => {
        const { queue, currentIndex } = get()
        const newQueue = [...queue]
        const [moved] = newQueue.splice(oldIndex, 1)
        newQueue.splice(newIndex, 0, moved)
        // インデックスを振り直す
        const reindexed = newQueue.map((t, i) => ({ ...t, index: i + 1 }))
        // 再生中トラックの位置を追跡
        let newCurrentIndex = currentIndex
        if (oldIndex === currentIndex) {
          newCurrentIndex = newIndex
        } else if (oldIndex < currentIndex && newIndex >= currentIndex) {
          newCurrentIndex = currentIndex - 1
        } else if (oldIndex > currentIndex && newIndex <= currentIndex) {
          newCurrentIndex = currentIndex + 1
        }
        set({ queue: reindexed, currentIndex: newCurrentIndex })
      },

      updatePlayCounts: (countMap) => {
        set((state) => ({
          queue: state.queue.map((track) => ({
            ...track,
            playCount: countMap.get(track.episodeId) ?? track.playCount,
          })),
        }))
      },

      incrementTrackPlayCount: (episodeId) => {
        const { queue, recordPlayHistory } = get()
        const track = queue.find((t) => t.episodeId === episodeId)
        if (track) {
          recordPlayHistory({
            episodeId: track.episodeId,
            title: track.title,
            thumbnailPath: track.thumbnailPath,
            artist: track.artist,
            album: track.album,
          })
        }
        set((state) => ({
          queue: state.queue.map((t) => (t.episodeId === episodeId ? { ...t, playCount: t.playCount + 1 } : t)),
        }))
      },

      recordPlayHistory: (entry) => {
        const { playHistory } = get()
        const updated = [
          { ...entry, playedAt: Date.now() },
          ...playHistory.filter((h) => h.episodeId !== entry.episodeId),
        ].slice(0, MAX_HISTORY)
        set({ playHistory: updated })
      },

      resetQueue: () => {
        const { queue, currentIndex } = get()
        const kept = queue.slice(0, currentIndex + 1)
        const reindexed = kept.map((t, i) => ({ ...t, index: i + 1 }))
        set({ queue: reindexed, _originalUpcoming: null })
      },

      skipToTrack: (targetIndex) => {
        const { queue, currentIndex } = get()
        if (targetIndex < 0 || targetIndex >= queue.length || targetIndex <= currentIndex) return
        // 現在のトラック + 選択先以降を残し、間を削除
        const kept = [...queue.slice(0, currentIndex + 1), ...queue.slice(targetIndex)]
        const reindexed = kept.map((t, i) => ({ ...t, index: i + 1 }))
        set({ queue: reindexed, currentIndex: currentIndex + 1 })
      },

      clearHistory: () => {
        set({ playHistory: [] })
      },

      clear: () => {
        set({ queue: [], currentIndex: 0, shuffle: false, repeatMode: 'off' })
      },
    }),
    {
      name: 'music-player',
      partialize: (state) => ({
        playHistory: state.playHistory,
        queue: state.queue,
        currentIndex: state.currentIndex,
        shuffle: state.shuffle,
        repeatMode: state.repeatMode,
        _originalUpcoming: state._originalUpcoming,
      }),
    },
  ),
)
